import type { PlayerProgress } from './types';

const WORKER_URL =
  (import.meta as any).env?.VITE_WORKER_URL ?? 'http://localhost:8787';

/** Valid charset: uppercase letters A-Z excluding 0, 1, O, I (ambiguous chars).
 *  Keeps the set to [A-Z2-9] — 32 symbols, 6 chars → 32^6 ≈ 1 billion combos. */
const CHARSET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** Generate a fresh 6-character family code. */
export function generateFamilyCode(): string {
  let code = '';
  const arr = new Uint8Array(6);
  crypto.getRandomValues(arr);
  for (const byte of arr) {
    code += CHARSET[byte % CHARSET.length];
  }
  return code;
}

/** Return true if `s` is exactly 6 characters from the valid charset. */
export function isValidFamilyCode(s: string): boolean {
  if (s.length !== 6) return false;
  for (const ch of s) {
    if (!CHARSET.includes(ch)) return false;
  }
  return true;
}

/**
 * Upload the current PlayerProgress to the Worker under the given family code.
 * Throws if the network request fails or the server returns a non-2xx status.
 */
export async function uploadProgress(
  familyCode: string,
  p: PlayerProgress,
): Promise<void> {
  const res = await fetch(`${WORKER_URL}/sync/${familyCode}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(p),
  });
  if (!res.ok) throw new Error(`upload failed: ${res.status}`);
}

/**
 * Download PlayerProgress from the Worker for the given family code.
 * Returns null if the family code has no stored data (404).
 * Throws if the network request fails or the server returns another non-2xx status.
 */
export async function downloadProgress(
  familyCode: string,
): Promise<PlayerProgress | null> {
  const res = await fetch(`${WORKER_URL}/sync/${familyCode}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`download failed: ${res.status}`);
  return (await res.json()) as PlayerProgress;
}
